'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { PageState } from '@/components/common/feedback/PageState';
import { useAuth } from '@/lib/auth';
import {
  Plus,
  RefreshCw,
  Ticket,
  Clock,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  getTickets,
  TicketListItem,
  TicketStatus,
  getStatusLabel,
  getStatusColor,
  getPriorityLabel,
  getPriorityColor,
  ApiClientError,
} from '@/services/api/tickets';

/**
 * 我的工单页面
 * 遵循飞书风格设计规范
 */
export default function MyTicketsPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const { user } = useAuth();

  // 状态
  const [activeTab, setActiveTab] = useState<'created' | 'assigned'>('created');
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // 加载工单
  const loadTickets = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = activeTab === 'created'
        ? { creatorId: user?.id, limit: 50 }
        : { assigneeId: user?.id, limit: 50 };

      const result = await getTickets(params);
      setTickets(result.items);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.tickets?.error?.loadFailed || '加载失败');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab, user?.id, t]);

  useEffect(() => {
    if (user?.id) {
      loadTickets();
    }
  }, [loadTickets, user?.id]);

  // 格式化时间
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const tabs = [
    { id: 'created', label: t.tickets?.my?.created || '我创建的' },
    { id: 'assigned', label: t.tickets?.my?.assigned || '分配给我的' },
  ];

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: '#3370ff' }} />
          <p className="mt-4 text-sm" style={{ color: '#8f959e' }}>
            {t.common?.loading || '加载中...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 - 飞书风格，标准 h-12 高度 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            {/* 左侧：Tab 切换 */}
            <div className="flex items-center gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as 'created' | 'assigned')}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: activeTab === tab.id ? 'rgba(51, 112, 255, 0.1)' : 'transparent',
                    color: activeTab === tab.id ? '#3370ff' : '#646a73'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.backgroundColor = '#f7f8fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 右侧：操作按钮 */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadTickets(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329'
                }}
                onMouseEnter={(e) => {
                  if (!refreshing) e.currentTarget.style.backgroundColor = '#eff0f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t.common?.refresh || '刷新'}
              </button>

              <button
                onClick={() => router.push('/tickets/new')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#3370ff',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1e5eff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3370ff';
                }}
              >
                <Plus className="w-4 h-4" />
                {t.tickets?.create || '新建工单'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 内容区域 - flex-1 自动占满剩余空间 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 工单列表卡片 */}
          <div className="bg-white rounded-lg" style={{ border: '1px solid #e5e6eb' }}>
            {tickets.length === 0 ? (
              <div className="p-12">
                <PageState
                  variant="empty"
                  title={t.tickets.empty}
                  description={
                    activeTab === 'created'
                      ? t.tickets.my.emptyCreated
                      : t.tickets.my.emptyAssigned
                  }
                  actionLabel={
                    activeTab === 'created' ? t.tickets.create : undefined
                  }
                  onAction={
                    activeTab === 'created'
                      ? () => router.push('/tickets/new')
                      : undefined
                  }
                  layout="center"
                  className="mx-auto w-full max-w-2xl border-0 bg-transparent"
                />
              </div>
            ) : (
              <div>
                {tickets.map((ticket, index) => {
                  const statusColor = getStatusColor(ticket.status);
                  const priorityColor = getPriorityColor(ticket.priority);

                  return (
                    <div
                      key={ticket.id}
                      onClick={() => router.push(`/tickets/${ticket.id}`)}
                      className="p-4 transition-colors cursor-pointer flex items-center gap-4"
                      style={{
                        borderBottom: index === tickets.length - 1 ? 'none' : '1px solid #e5e6eb'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                    >
                      {/* 优先级指示条 */}
                      <div
                        className="w-1 h-12 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: priorityColor.bg.includes('red') ? '#f53f3f' :
                            priorityColor.bg.includes('orange') ? '#ff7d00' :
                            priorityColor.bg.includes('blue') ? '#3370ff' : '#86909c'
                        }}
                      />

                      {/* 内容 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono flex-shrink-0" style={{ color: '#8f959e' }}>
                            {ticket.ticketNo}
                          </span>
                          <h3 className="text-sm font-medium truncate" style={{ color: '#1f2329' }}>
                            {ticket.title}
                          </h3>
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0"
                            style={{
                              backgroundColor: statusColor.bg.includes('blue') ? 'rgba(51, 112, 255, 0.1)' :
                                statusColor.bg.includes('yellow') ? 'rgba(255, 125, 0, 0.1)' :
                                statusColor.bg.includes('purple') ? 'rgba(151, 117, 250, 0.1)' :
                                statusColor.bg.includes('green') ? 'rgba(0, 180, 42, 0.1)' : 'rgba(134, 144, 156, 0.1)',
                              color: statusColor.text.includes('blue') ? '#3370ff' :
                                statusColor.text.includes('yellow') ? '#ff7d00' :
                                statusColor.text.includes('purple') ? '#9775fa' :
                                statusColor.text.includes('green') ? '#00b42a' : '#86909c'
                            }}
                          >
                            {getStatusLabel(ticket.status, t)}
                          </span>
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0"
                            style={{
                              backgroundColor: priorityColor.bg.includes('red') ? 'rgba(245, 63, 63, 0.1)' :
                                priorityColor.bg.includes('orange') ? 'rgba(255, 125, 0, 0.1)' :
                                priorityColor.bg.includes('blue') ? 'rgba(51, 112, 255, 0.1)' : 'rgba(134, 144, 156, 0.1)',
                              color: priorityColor.text.includes('red') ? '#f53f3f' :
                                priorityColor.text.includes('orange') ? '#ff7d00' :
                                priorityColor.text.includes('blue') ? '#3370ff' : '#86909c'
                            }}
                          >
                            {getPriorityLabel(ticket.priority, t)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs" style={{ color: '#8f959e' }}>
                          {ticket.category && <span>{ticket.category.name}</span>}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDate(ticket.createdAt)}
                          </span>
                          {activeTab === 'created' && ticket.assignee && (
                            <span>{t.tickets?.field?.assignee || '处理人'}: {ticket.assignee.displayName}</span>
                          )}
                          {activeTab === 'assigned' && ticket.creator && (
                            <span>{t.tickets?.field?.creator || '创建人'}: {ticket.creator.displayName}</span>
                          )}
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#c9cdd4' }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
